import { useTheme } from '@pixee/design-system'
import classNames from 'classnames'
import React from 'react'
import { match } from 'ts-pattern'
import AikidoLogoDk from '../../assets/SastToolVisualAssets/Aikido-Logo-Dk.png?url'
import AikidoLogoLt from '../../assets/SastToolVisualAssets/Aikido-Logo-Lt.png?url'
import AppScanLogoDk from '../../assets/SastToolVisualAssets/AppScan-Logo-Dk.png?url'
import AppScanLogoLt from '../../assets/SastToolVisualAssets/AppScan-Logo-Lt.png?url'
import ArnicaLogoDk from '../../assets/SastToolVisualAssets/Arnica-Logo-Dk.svg?react'
import ArnicaLogoLt from '../../assets/SastToolVisualAssets/Arnica-Logo-Lt.svg?react'
import CheckmarxLogoDk from '../../assets/SastToolVisualAssets/Checkmarx-Logo-Dk.png?url'
import CheckmarxLogoLt from '../../assets/SastToolVisualAssets/Checkmarx-Logo-Lt.png?url'
import CheckmarxEnterpriseLogoDk from '../../assets/SastToolVisualAssets/CheckmarxEnterprise-Logo-Dk.png?url'
import CheckmarxEnterpriseLogoLt from '../../assets/SastToolVisualAssets/CheckmarxEnterprise-Logo-Lt.png?url'
import CodeQLLogo from '../../assets/SastToolVisualAssets/CodeQL-Logo.svg?react'
import ContrastLogoDk from '../../assets/SastToolVisualAssets/Contrast-Logo-Dk.svg?react'
import ContrastLogoLt from '../../assets/SastToolVisualAssets/Contrast-Logo-Lt.svg?react'
import DatadogLogoDk from '../../assets/SastToolVisualAssets/Datadog-Logo-Dk.svg?react'
import DatadogLogoLt from '../../assets/SastToolVisualAssets/Datadog-Logo-Lt.svg?react'
import FortifyLogoDk from '../../assets/SastToolVisualAssets/Fortify-Logo-Dk.png?url'
import FortifyLogoLt from '../../assets/SastToolVisualAssets/Fortify-Logo-Lt.png?url'
import DependabotLogoDk from '../../assets/SastToolVisualAssets/GitHub-Dependabot-Logo-Dk.svg?react'
import DependabotLogoLt from '../../assets/SastToolVisualAssets/GitHub-Dependabot-Logo-Lt.svg?react'
import GitLabSASTLogoDk from '../../assets/SastToolVisualAssets/GitLab-SAST-Logo-Dk.svg?react'
import GitLabSASTLogoLt from '../../assets/SastToolVisualAssets/GitLab-SAST-Logo-Lt.svg?react'
import GitLabSCALogoDk from '../../assets/SastToolVisualAssets/GitLab-SCA-Logo-Dk.svg?react'
import GitLabSCALogoLt from '../../assets/SastToolVisualAssets/GitLab-SCA-Logo-Lt.svg?react'
import GitleaksLogo from '../../assets/SastToolVisualAssets/Gitleaks-Icon.png?url'
import PixeeLogoDk from '../../assets/SastToolVisualAssets/Pixee-Logo-Dk.png?url'
import PixeeLogoLt from '../../assets/SastToolVisualAssets/Pixee-Logo-Lt.png?url'
import PolarisLogoDk from '../../assets/SastToolVisualAssets/Polaris-Logo-Dk.png?url'
import PolarisLogoLt from '../../assets/SastToolVisualAssets/Polaris-Logo-Lt.png?url'
import SemgrepLogoDk from '../../assets/SastToolVisualAssets/Semgrep-Logo-Dk.svg?react'
import SemgrepLogoLt from '../../assets/SastToolVisualAssets/Semgrep-Logo-Lt.svg?react'
import SnykLogoDk from '../../assets/SastToolVisualAssets/Snyk-Logo-Dk.svg?react'
import SnykLogoLt from '../../assets/SastToolVisualAssets/Snyk-Logo-Lt.svg?react'
import SonarLogoDk from '../../assets/SastToolVisualAssets/Sonar-Logo-Dk.svg?react'
import SonarLogoLt from '../../assets/SastToolVisualAssets/Sonar-Logo-Lt.svg?react'
import VeracodeLogoDk from '../../assets/SastToolVisualAssets/Veracode-Logo-Dk.png?url'
import VeracodeLogoLt from '../../assets/SastToolVisualAssets/Veracode-Logo-Lt.png?url'
import ZapLogoDk from '../../assets/SastToolVisualAssets/ZAP-Logo-Dk.svg?react'
import ZapLogoLt from '../../assets/SastToolVisualAssets/ZAP-Logo-Lt.svg?react'
import type { Tool } from '../../utils/api-client/api-schemas'
import { FindingsIntakeIcon } from './findings-intake-icon'
import * as styles from './tool-logo.css.ts'
import type { ToolBrandingSize } from './tool-utils'

export const ARIA_LABELS = {
  aikido_sast: 'Aikido SAST logo',
  aikido_sca: 'Aikido SCA logo',
  appscan: 'AppScan logo',
  checkmarx: 'Checkmarx logo',
  checkmarx_enterprise: 'Checkmarx Enterprise logo',
  codeql: 'CodeQL logo',
  contrast: 'Contrast logo',
  fortify: 'Fortify logo',
  gitlab_sast: 'GitLab SAST logo',
  gitlab_dependency_scanning: 'GitLab SCA logo',
  dependabot: 'Dependabot logo',
  polaris_sast: 'Polaris SAST logo',
  polaris_sca: 'Polaris SCA logo',
  black_duck_hub_sca: 'Black Duck Hub SCA logo',
  semgrep: 'Semgrep logo',
  sonar: 'SonarQube logo',
  snyk: 'Snyk logo',
  veracode: 'Veracode logo',
  datadog_sast: 'Datadog SAST logo',
  arnica_sast: 'Arnica logo',
  gitleaks: 'Gitleaks logo',
  zap: 'OWASP ZAP logo',
  pixee_ai: 'Pixee Beacon logo',
  freeform: 'Findings intake logo',
} satisfies Record<Tool, string>

export type ToolLogoProps = {
  tool: Tool
  size?: ToolBrandingSize
  className?: string
  withAriaLabel?: boolean
}

/**
 * Returns the appropriate tool logo component based on the detector type
 * @param detector - The detector type
 * @param size - The height of the logo (small 16px, medium 20px, medium+ 24px, large 32px, large+ 64px)
 * @param className - Optional CSS class name to apply to the logo
 * @param withAriaLabel - Whether to add an aria-label to the logo. Turn on only if relevant to screen reader users (and tests).
 * @returns A React component representing the tool's logo
 */
export const ToolLogo = ({ tool, size = 'medium', className, withAriaLabel = false }: ToolLogoProps) => {
  const sharedProps = {
    tool,
    size,
    withAriaLabel,
    className,
  } satisfies Omit<React.ComponentProps<typeof ToolLogoPng>, 'lightSrc' | 'darkSrc'> &
    Omit<React.ComponentProps<typeof ToolLogoSvg>, 'lightComponent' | 'darkComponent'>

  return (
    match(tool)
      .with('aikido_sast', 'aikido_sca', () => (
        <ToolLogoPng lightSrc={AikidoLogoLt} darkSrc={AikidoLogoDk} {...sharedProps} />
      ))
      .with('appscan', () => <ToolLogoPng lightSrc={AppScanLogoLt} darkSrc={AppScanLogoDk} {...sharedProps} />)
      .with('checkmarx', () => <ToolLogoPng lightSrc={CheckmarxLogoLt} darkSrc={CheckmarxLogoDk} {...sharedProps} />)
      .with('checkmarx_enterprise', () => (
        <ToolLogoPng lightSrc={CheckmarxEnterpriseLogoLt} darkSrc={CheckmarxEnterpriseLogoDk} {...sharedProps} />
      ))
      .with('codeql', () => <ToolLogoSvg lightComponent={CodeQLLogo} darkComponent={CodeQLLogo} {...sharedProps} />)
      .with('contrast', () => (
        <ToolLogoSvg lightComponent={ContrastLogoLt} darkComponent={ContrastLogoDk} {...sharedProps} />
      ))
      .with('fortify', () => <ToolLogoPng lightSrc={FortifyLogoLt} darkSrc={FortifyLogoDk} {...sharedProps} />)
      .with('gitlab_sast', () => (
        <ToolLogoSvg lightComponent={GitLabSASTLogoLt} darkComponent={GitLabSASTLogoDk} {...sharedProps} />
      ))
      .with('gitlab_dependency_scanning', () => (
        <ToolLogoSvg lightComponent={GitLabSCALogoLt} darkComponent={GitLabSCALogoDk} {...sharedProps} />
      ))
      .with('dependabot', () => (
        <ToolLogoSvg lightComponent={DependabotLogoLt} darkComponent={DependabotLogoDk} {...sharedProps} />
      ))
      .with('polaris_sast', 'polaris_sca', 'black_duck_hub_sca', () => (
        <ToolLogoPng lightSrc={PolarisLogoLt} darkSrc={PolarisLogoDk} {...sharedProps} />
      ))
      .with('semgrep', () => (
        <ToolLogoSvg lightComponent={SemgrepLogoLt} darkComponent={SemgrepLogoDk} {...sharedProps} />
      ))
      .with('sonar', () => <ToolLogoSvg lightComponent={SonarLogoLt} darkComponent={SonarLogoDk} {...sharedProps} />)
      .with('snyk', () => <ToolLogoSvg lightComponent={SnykLogoLt} darkComponent={SnykLogoDk} {...sharedProps} />)
      .with('veracode', () => <ToolLogoPng lightSrc={VeracodeLogoLt} darkSrc={VeracodeLogoDk} {...sharedProps} />)
      .with('datadog_sast', () => (
        <ToolLogoSvg lightComponent={DatadogLogoLt} darkComponent={DatadogLogoDk} {...sharedProps} />
      ))
      .with('arnica_sast', () => (
        <ToolLogoSvg lightComponent={ArnicaLogoLt} darkComponent={ArnicaLogoDk} {...sharedProps} />
      ))
      .with('gitleaks', () => (
        <ToolLogoPng lightSrc={GitleaksLogo} darkSrc={GitleaksLogo} monochromeBackdrop {...sharedProps} />
      ))
      .with('zap', () => <ToolLogoSvg lightComponent={ZapLogoLt} darkComponent={ZapLogoDk} {...sharedProps} />)
      .with('pixee_ai', () => <ToolLogoPng lightSrc={PixeeLogoLt} darkSrc={PixeeLogoDk} {...sharedProps} />)
      // Findings intake is Pixee-internal with no wordmark, so its logo is the same tile as its icon.
      .with('freeform', () => (
        <ToolLogoSvg lightComponent={FindingsIntakeIcon} darkComponent={FindingsIntakeIcon} {...sharedProps} />
      ))
      .exhaustive()
  )
}

type SharedToolLogoProps = Omit<ToolLogoProps, 'size'> & {
  size: ToolBrandingSize
}

const ToolLogoPng = ({
  lightSrc,
  darkSrc,
  tool,
  size,
  withAriaLabel,
  className,
  monochromeBackdrop = false,
}: SharedToolLogoProps & {
  lightSrc: string
  darkSrc: string
  monochromeBackdrop?: boolean
}) => {
  const { theme } = useTheme()
  const src = theme === 'dark' ? darkSrc : lightSrc
  const monochromeStyle = !monochromeBackdrop
    ? undefined
    : match(theme)
        .with('dark', () => ({ backgroundColor: 'white' }))
        .otherwise(() => ({ backgroundColor: 'black', filter: 'invert(1)' }))

  return (
    <span className={styles.logoBox({ size })}>
      <img
        src={src}
        data-testid={`${tool}-logo-${theme}`}
        alt={withAriaLabel ? ARIA_LABELS[tool] : ''}
        aria-label={withAriaLabel ? ARIA_LABELS[tool] : undefined}
        className={classNames(styles.logoImage({ size, tool }), className)}
        style={monochromeStyle}
      />
    </span>
  )
}

// Copied from vite-plugin-svgr/client.d.ts
type SvgComponent = React.FunctionComponent<
  React.ComponentProps<'svg'> & { title?: string; titleId?: string; desc?: string; descId?: string }
>

const ToolLogoSvg = ({
  lightComponent,
  darkComponent,
  tool,
  size,
  withAriaLabel,
  className,
}: SharedToolLogoProps & {
  lightComponent: SvgComponent
  darkComponent: SvgComponent
}) => {
  const { theme } = useTheme()
  const Component = theme === 'dark' ? darkComponent : lightComponent

  return (
    <span className={styles.logoBox({ size })}>
      <Component
        data-testid={`${tool}-logo-${theme}`}
        aria-label={withAriaLabel ? ARIA_LABELS[tool] : undefined}
        className={classNames(styles.logoImage({ size, tool }), className)}
      />
    </span>
  )
}
